import React, { useEffect, useMemo, useState } from "react";
import { MessageSquare, Loader2, Trash2, CornerDownRight, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { isAdmin, adminHeader } from "../lib/admin";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import MentionInput from "./MentionInput";
import MentionText from "./MentionText";
import { GifReactionPickerButton, InlineGif } from "./themes/GifReactionPicker";

const LOCAL_NAME_KEY = "vault.comment.name";
const MAX_DEPTH = 4;

/**
 * CommentBody — splits a comment's text on `[gif:URL]` tokens and renders
 * each fragment with MentionText (so @-mentions still link) and each GIF
 * token as an InlineGif. Single function so we never accidentally lose
 * mention linkification by piping text through a different renderer.
 */
function CommentBody({ content }) {
  if (!content) return null;
  const re = /\[gif:(https?:\/\/[^\]\s]+)\]/g;
  const parts = [];
  let last = 0; let i = 0; let m;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) parts.push({ k: `t${i++}`, type: "text", value: content.slice(last, m.index) });
    parts.push({ k: `g${i++}`, type: "gif", value: m[1] });
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push({ k: `t${i++}`, type: "text", value: content.slice(last) });
  if (parts.length === 0) return <MentionText text={content} />;
  return parts.map((p) =>
    p.type === "gif"
      ? <InlineGif key={p.k} url={p.value} />
      : <span key={p.k}><MentionText text={p.value} /></span>
  );
}

/**
 * Comments — flat thread feed with one-level-collapse-able nested replies.
 *
 * Backend now stores `parent_id` on each comment and returns `reply_count`.
 * We fetch the full flat list (sorted oldest-first) and build a tree client-side
 * so we can render reply indentation without an N+1 query.
 */
export default function Comments({ targetType, targetId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(
    () => (user?.username || localStorage.getItem(LOCAL_NAME_KEY) || "")
  );
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [admin, setAdmin] = useState(isAdmin());
  const [replyOpen, setReplyOpen] = useState(null);  // comment id we're replying to
  const [replyText, setReplyText] = useState("");
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  useEffect(() => {
    if (user?.username) setName(user.username);
  }, [user?.username]);

  useEffect(() => {
    const h = () => setAdmin(isAdmin());
    window.addEventListener("admin-changed", h);
    return () => window.removeEventListener("admin-changed", h);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/comments/${targetType}/${targetId}`);
      setItems(r.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, targetId]);

  // Build tree
  const tree = useMemo(() => {
    const byParent = new Map();
    for (const c of items) {
      const key = c.parent_id || "__root__";
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(c);
    }
    return byParent;
  }, [items]);

  const submit = async (e) => {
    e?.preventDefault();
    const c = content.trim();
    if (!c || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/comments", {
        target_type: targetType,
        target_id: targetId,
        author: name.trim() || null,
        content: c,
      });
      setItems((prev) => [...prev, r.data]);
      setContent("");
      if (name.trim()) localStorage.setItem(LOCAL_NAME_KEY, name.trim());
      toast.success("Comment posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentId) => {
    const c = (replyText || "").trim();
    if (!c) return;
    try {
      const r = await api.post("/comments", {
        target_type: targetType,
        target_id: targetId,
        author: name.trim() || null,
        content: c,
        parent_id: parentId,
      });
      setItems((prev) => [...prev, r.data]);
      // Bump the parent's reply_count locally so UI updates instantly.
      setItems((prev) => prev.map((it) =>
        it.id === parentId ? { ...it, reply_count: (it.reply_count || 0) + 1 } : it
      ));
      setReplyText("");
      setReplyOpen(null);
      toast.success("Reply posted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reply");
    }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toISOString().slice(0, 16).replace("T", " ");
    } catch { return ""; }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/admin/comments/${id}`, { headers: adminHeader() });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Comment deleted");
    } catch { toast.error("Delete failed"); }
  };

  const toggleCollapse = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const renderNode = (c, depth = 0) => {
    const children = tree.get(c.id) || [];
    const collapsed = collapsedIds.has(c.id);
    const indent = depth >= MAX_DEPTH - 1 ? MAX_DEPTH - 1 : depth;
    return (
      <li key={c.id} data-testid={`comment-${c.id}`}>
        <div
          className="border border-white/10 bg-[#0F0F0F] p-4"
          style={{ marginLeft: indent * 24 }}
        >
          <div className="flex items-center justify-between mb-2 font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            <span className={c.is_anon ? "text-zinc-500 uppercase tracking-[0.2em]" : "text-[#ffd60a]"}>
              {depth > 0 && <CornerDownRight size={10} className="inline mr-1 -mt-0.5 text-[#60a5fa]" />}
              {c.is_anon ? `// ${c.author || "REDACTED"}` : c.author}
            </span>
            <div className="flex items-center gap-3">
              <span>{fmt(c.created_at)} UTC</span>
              {admin && (
                <button onClick={() => onDelete(c.id)} data-testid={`admin-delete-comment-${c.id}`}
                        className="text-zinc-500 hover:text-[#ff3b30]" aria-label="Delete comment">
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          </div>
          <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words space-y-2">
            <CommentBody content={c.content || ""} />
          </div>
          {/* Reply controls */}
          <div className="mt-3 flex items-center gap-3">
            {user && depth < MAX_DEPTH - 1 && (
              <button
                type="button"
                data-testid={`reply-toggle-${c.id}`}
                onClick={() => { setReplyOpen(replyOpen === c.id ? null : c.id); setReplyText(""); }}
                className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#60a5fa] inline-flex items-center gap-1"
              >
                <MessageSquare size={10} /> Reply
              </button>
            )}
            {children.length > 0 && (
              <button
                type="button"
                data-testid={`collapse-${c.id}`}
                onClick={() => toggleCollapse(c.id)}
                className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#60a5fa] inline-flex items-center gap-1"
              >
                {collapsed ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                {collapsed ? `SHOW ${children.length} REPL${children.length === 1 ? "Y" : "IES"}` : "HIDE THREAD"}
              </button>
            )}
          </div>
          {/* Reply composer */}
          {replyOpen === c.id && (
            <div className="mt-3 pt-3 border-t border-white/10" data-testid={`reply-composer-${c.id}`}>
              <MentionInput
                value={replyText} onChange={setReplyText} rows={2} maxLength={2000}
                placeholder={`Reply to ${c.author}…`}
                dataTestId={`reply-input-${c.id}`}
                className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono text-xs text-white placeholder-zinc-600 focus:border-[#60a5fa] focus:outline-none resize-none"
              />
              <div className="flex items-center justify-between gap-2 mt-2">
                <GifReactionPickerButton
                  testIdPrefix={`reply-gif-${c.id}`}
                  onPick={(url) => setReplyText((t) => (t ? t + " " : "") + `[gif:${url}]`)}
                />
                <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setReplyOpen(null); setReplyText(""); }}
                        className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white px-2 py-1">
                  Cancel
                </button>
                <button type="button" onClick={() => submitReply(c.id)}
                        disabled={!replyText.trim()}
                        data-testid={`reply-submit-${c.id}`}
                        className="inline-flex items-center gap-1 bg-[#60a5fa] text-[#050505] hover:bg-white disabled:opacity-30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors">
                  Reply
                </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Child replies */}
        {!collapsed && children.length > 0 && (
          <ul className="mt-3 space-y-3">
            {children.map((ch) => renderNode(ch, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  const roots = tree.get("__root__") || [];

  return (
    <section data-testid="comments-section" className="mt-12">
      <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3 flex items-center gap-2">
        <MessageSquare size={11} /> ANONYMOUS CHATTER // {items.length}
      </div>
      <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-6 border-b border-white/10 pb-3">
        Reader Comments
      </h3>

      <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 p-5 mb-8 space-y-3">
        <div className="grid sm:grid-cols-[200px_1fr] gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Handle (optional)"
            maxLength={40}
            data-testid="comment-author-input"
            className="bg-transparent border border-white/15 px-3 py-2 font-mono text-xs text-white placeholder-zinc-600 focus:border-[#ff3b30] focus:outline-none"
          />
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 self-center">
            LEAVE BLANK FOR "ANON" — NO ACCOUNT, NO TRACKING
          </div>
        </div>
        <MentionInput
          value={content}
          onChange={setContent}
          rows={3}
          maxLength={2000}
          placeholder="Share a thought, an angle, a source link, a question…  type @ to mention another member"
          dataTestId="comment-content-input"
          className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono text-xs text-white placeholder-zinc-600 focus:border-[#ff3b30] focus:outline-none resize-none"
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] tracking-wider text-zinc-600">
              {content.length} / 2000
            </span>
            <GifReactionPickerButton
              testIdPrefix="comment-gif"
              onPick={(url) => setContent((c) => (c ? c + " " : "") + `[gif:${url}]`)}
            />
          </div>
          <button
            type="submit"
            disabled={!content.trim() || submitting}
            data-testid="comment-submit"
            className="inline-flex items-center gap-2 bg-[#f5f5f5] text-[#050505] hover:bg-[#ff3b30] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
            Post Comment
          </button>
        </div>
      </form>

      {loading ? (
        <div className="font-mono text-xs tracking-wider text-zinc-500">LOADING...</div>
      ) : roots.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-dashed border-white/10 p-8 text-center font-mono text-[10px] tracking-[0.2em] text-zinc-600">
          NO COMMENTS YET — BE THE FIRST.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="comments-list">
          {roots.map((c) => renderNode(c, 0))}
        </ul>
      )}
    </section>
  );
}
